import React, { useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Factory,
  Users,
  Settings,
  Shield,
  ChevronRight,
  ChevronDown,
  Package,
  Calendar,
  BarChart3,
  BoxSelect,
  Search,
  TreePine,
  Zap,
  FileText,
  Clock,
  UserCheck,
  CalendarDays,
  ClipboardList,
  UserPlus,
  DoorOpen,
  CheckSquare,
  Database,
  Sliders,
  GitBranch,
  BookOpen,
  Gauge,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Award,
  ShieldAlert,
  ScrollText,
  AlertOctagon,
  GraduationCap,
  HardHat,
  Heart,
  Building2,
  Timer,
  Globe,
  Ruler,
  DollarSign,
  RefreshCw,
  Monitor,
  User,
  Wrench,
} from 'lucide-react'
import { cn } from '../lib/utils'
import { usePermissions } from '../hooks/usePermissions'
import { filterSidebarMenuByAccess } from '../utils/routeAccess'
import { useLanguage } from '../context/LanguageContext'

/** Definición del menú: labelKey apunta a t.navigation.* */
const FULL_SIDEBAR_MENU = [
  {
    id: 'dashboard',
    labelKey: 'dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    id: 'productive',
    labelKey: 'productive',
    icon: Package,
    submenu: [
      { labelKey: 'orders', path: '/ordenes-fabricacion', icon: Package },
      { labelKey: 'scheduling', path: '/programacion', icon: Calendar },
      { labelKey: 'oee', path: '/oee-metricas', icon: BarChart3 },
      { labelKey: 'materials', path: '/materiales-consumos', icon: BoxSelect },
      { labelKey: 'traceability', path: '/lotes-trazabilidad', icon: Search },
    ]
  },
  {
    id: 'assets',
    labelKey: 'assets',
    icon: Factory,
    submenu: [
      { labelKey: 'assetHierarchy', path: '/jerarquia-activos', icon: TreePine },
      { labelKey: 'lines', path: '/lineas', icon: Factory },
      { labelKey: 'availabilityStates', path: '/estados-disponibilidad', icon: Zap },
      { labelKey: 'technicalSpecs', path: '/especificaciones-tecnicas', icon: FileText },
      { labelKey: 'assetsRegistration', path: '/asset-management', icon: Settings },
      { labelKey: 'productsRegistration', path: '/products', icon: Package },
    ]
  },
  {
    id: 'people',
    labelKey: 'people',
    icon: Users,
    submenu: [
      { labelKey: 'laborCalendar', path: '/calendario-labor', icon: CalendarDays },
      { labelKey: 'employees', path: '/empleados', icon: UserCheck },
      { labelKey: 'attendance', path: '/asistencia', icon: ClipboardList },
      { labelKey: 'assignments', path: '/asignaciones', icon: UserPlus },
      { labelKey: 'specialExits', path: '/salidas-especiales', icon: DoorOpen },
      { labelKey: 'hoursControl', path: '/control-horas', icon: Timer },
      { labelKey: 'evaluations', path: '/evaluaciones', icon: CheckSquare },
    ]
  },
  {
    id: 'processes',
    labelKey: 'processes',
    icon: GitBranch,
    submenu: [
      { labelKey: 'masterData', path: '/datos-maestros-procesos', icon: Database },
      { labelKey: 'processParameters', path: '/parametros-procesos', icon: Sliders },
      { labelKey: 'bomRoutes', path: '/bom-rutas', icon: GitBranch },
      { labelKey: 'workInstructions', path: '/instrucciones-trabajo', icon: BookOpen },
      { labelKey: 'events', path: '/events', icon: Calendar },
      { labelKey: 'energy', path: '/energy', icon: Zap },
    ]
  },
  {
    id: 'quality',
    labelKey: 'quality',
    icon: Award,
    submenu: [
      { labelKey: 'inspectionPlans', path: '/planes-inspeccion', icon: CheckCircle2 },
      { labelKey: 'qualityCharacteristics', path: '/caracteristicas-calidad', icon: CheckCircle2 },
      { labelKey: 'defects', path: '/defectos', icon: AlertTriangle },
      { labelKey: 'statisticalControl', path: '/control-estadistico', icon: TrendingUp },
      { labelKey: 'correctiveActions', path: '/acciones-correctivas', icon: AlertTriangle },
      { labelKey: 'certificates', path: '/certificados', icon: Award },
    ]
  },
  {
    id: 'maintenance',
    labelKey: 'maintenance',
    icon: Wrench,
    submenu: [
      { labelKey: 'maintenancePlans', path: '/planes-mantenimiento', icon: Wrench },
      { labelKey: 'assignmentPlans', path: '/planes-asignacion', icon: UserCheck },
      { labelKey: 'executionPlans', path: '/planes-ejecucion', icon: CheckCircle2 },
      { labelKey: 'spareParts', path: '/almacen-repuestos', icon: BoxSelect },
      { labelKey: 'kpi', path: '/kpi', icon: Gauge },
    ]
  },
  {
    id: 'safety',
    labelKey: 'safety',
    icon: ShieldAlert,
    submenu: [
      { labelKey: 'risks', path: '/riesgos', icon: AlertTriangle },
      { labelKey: 'safetyInspections', path: '/inspecciones-seguridad', icon: ScrollText },
      { labelKey: 'accidents', path: '/accidentes', icon: AlertOctagon },
      { labelKey: 'trainings', path: '/capacitaciones', icon: GraduationCap },
      { labelKey: 'ppe', path: '/epp', icon: HardHat },
      { labelKey: 'occupationalHealth', path: '/salud-ocupacional', icon: Heart },
    ]
  },
  {
    id: 'configuration',
    labelKey: 'configuration',
    icon: Settings,
    submenu: [
      { labelKey: 'companyConfig', path: '/config-empresa', icon: Building2 },
      { labelKey: 'calendarConfig', path: '/config-calendario', icon: Calendar },
      { labelKey: 'shiftsConfig', path: '/config-turnos', icon: Clock },
      { labelKey: 'timezonesConfig', path: '/config-zonas-horarias', icon: Globe },
      { labelKey: 'unitsConfig', path: '/config-unidades', icon: Ruler },
      { labelKey: 'currenciesConfig', path: '/config-monedas', icon: DollarSign },
      { labelKey: 'employeeManagement', path: '/gestion-empleados', icon: UserCheck },
      { labelKey: 'externalConfig', path: '/config-external', icon: UserCheck },
      { labelKey: 'automaticProcesses', path: '/procesos-automaticos', icon: RefreshCw },
      { labelKey: 'dashboards', path: '/grafana-test', icon: Monitor },
    ]
  },
  {
    id: 'system',
    labelKey: 'system',
    icon: Shield,
    submenu: [
      { labelKey: 'security', path: '/seguridad', icon: Monitor },
      { labelKey: 'logs', path: '/logs', icon: FileText },
      { labelKey: 'users', path: '/usuarios', icon: User },
    ]
  },
]

function resolveNavLabel(t, labelKey) {
  return t?.navigation?.[labelKey] || labelKey
}

function localizeMenu(menuItems, t) {
  return menuItems.map((item) => {
    const name = resolveNavLabel(t, item.labelKey)
    if (item.submenu) {
      return {
        ...item,
        name,
        submenu: item.submenu.map((sub) => ({
          ...sub,
          name: resolveNavLabel(t, sub.labelKey),
        })),
      }
    }
    return { ...item, name }
  })
}

function Sidebar({ isVisible = true }) {
  const location = useLocation()
  const [expandedMenus, setExpandedMenus] = useState({})
  const { permissions, isLoading } = usePermissions()
  const { t } = useLanguage()

  const menuStructure = useMemo(() => {
    if (isLoading || !permissions) {
      return []
    }
    const localized = localizeMenu(FULL_SIDEBAR_MENU, t)
    return filterSidebarMenuByAccess(localized, permissions)
  }, [permissions, isLoading, t])

  const withCurrentQuery = (path) => ({
    pathname: path,
    search: location.search,
  })

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }))
  }

  return (
    <aside className="w-64 bg-gray-800 border-r border-gray-700 flex flex-col h-full min-h-0">
      {/* Navegación Principal: scroll interno cuando el menú es largo */}
      <nav className="flex-1 min-h-0 px-3 py-4 space-y-1 overflow-y-auto">
        {menuStructure.map((item) => {
          const Icon = item.icon
          const menuKey = item.id || item.labelKey || item.name
          const isExpanded = expandedMenus[menuKey]

          // Si tiene submenú
          if (item.submenu) {
            return (
              <div key={menuKey}>
                {/* Parent Menu Item */}
                <button
                  onClick={() => toggleMenu(menuKey)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium',
                    'text-gray-300 hover:bg-gray-700 hover:text-white'
                  )}
                >
                  <Icon size={18} />
                  <span className="flex-1 text-left">{item.name}</span>
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400" />
                  )}
                </button>

                {/* Submenu Items */}
                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.submenu.map((subitem) => {
                      const SubIcon = subitem.icon
                      return (
                        <NavLink
                          key={subitem.path}
                          to={withCurrentQuery(subitem.path)}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-xs font-medium',
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                            )
                          }
                        >
                          <SubIcon size={16} />
                          <span className="truncate">{subitem.name}</span>
                        </NavLink>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }

          // Si es un item simple (sin submenú)
          return (
            <NavLink
              key={item.path}
              to={withCurrentQuery(item.path)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium',
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} />
                  <span>{item.name}</span>
                  {isActive && <ChevronRight size={16} className="ml-auto" />}
                </>
              )}
            </NavLink>
          )
        })}
      </nav>
    </aside>
  )
}

export default Sidebar
